import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Loader2, 
  ScrollText, 
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { getAuditLogs } from '@/api/project';
import type { AuditLog } from '@/types/project';

function AuditLogRow({ log }: { log: AuditLog }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="py-3 px-4 border-b text-sm">
        {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
      </td>
      <td className="py-3 px-4 border-b">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
            <User className="h-4 w-4 text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-medium">{log.user_name || log.user_email || 'System'}</p>
            {log.user_email && log.user_name && (
              <p className="text-xs text-gray-500">{log.user_email}</p>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4 border-b">
        <p className="text-sm">{log.message || log.action}</p>
        {log.resource_type && (
          <p className="text-xs text-gray-500">
            {log.resource_type}
            {log.resource_id && ` #${log.resource_id}`}
          </p>
        )}
      </td>
    </tr>
  );
}

export function AuditLogsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [page, setPage] = useState(1);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-logs', projectId, page],
    queryFn: () => getAuditLogs(projectId!, page),
    enabled: !!projectId,
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load audit logs. You may not have permission to view this page.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const logs = data?.logs || [];
  const pagination = data?.pagination;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ScrollText className="h-6 w-6" />
            Audit Logs
          </h1>
          <p className="text-gray-500 mt-1">
            Track all actions and changes in your project
          </p>
        </div>

        {/* Audit Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>
              {pagination?.total || 0} total entries
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {logs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ScrollText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No audit logs yet</p>
                <p className="text-sm">Activity will be logged here as changes are made.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b" style={{ width: '180px' }}>
                          Time
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b" style={{ width: '250px' }}>
                          User
                        </th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <AuditLogRow key={log.id} log={log} />
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {pagination && pagination.pages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-sm text-gray-500">
                      Showing {logs.length} of {pagination.total} entries
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={!pagination.has_prev}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Prev
                      </Button>
                      <span className="text-sm text-gray-500">
                        Page {pagination.page} of {pagination.pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={!pagination.has_next}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
